import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { apiLogin, apiRegister, apiMe, api2faVerify, apiRecoverySetup, apiSetLanguage, apiRedeemStackSocialCode, apiRedeemStackSocialCodeSelf,
         apiUpdateProfile, apiRecoveryVerify, apiRecoveryReset, apiCampioneRecupero } from '../utils/api';
import { generateRecoveryToken, deriveRecoveryKey, encryptKeyWithRecovery, hashRecoveryToken } from '../utils/crypto';
import { deriveKey, generateSalt } from '../utils/crypto';
// Chiave principale (ENCRYPTION-PLAN.md 3-ter). Spento per tutti finche'
// REACT_APP_CHIAVE_PRINCIPALE_UTENTI non elenca qualcuno: con la variabile
// assente apriChiaveUtente() e' letteralmente deriveKey(), e migraSeServe()
// esce subito. Nessun comportamento cambia per nessuno.
import { apriChiaveUtente, migraSeServe, BustaNonApribile,
         reincartaConNuovaPassword, recuperaChiaveDaBackup, provaChiaveSuCampioni } from '../utils/chiavePrincipale';
import { getAttribution, getReferralCode } from '../utils/attribution';
import i18n from '../i18n';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storage';

const AuthContext = createContext(null);
const SESSION_PWD_KEY = 'zv_pwd_session';

// La busta non si apre. Decisione presa il 2026-09-10: si cancella tutto
// quello che l'accesso ha appena scritto e si mostra un errore. NON si
// ripiega sulla derivazione vecchia — vedi il commento in
// utils/chiavePrincipale.js: il ripiego funzionerebbe oggi e diventerebbe un
// danno invisibile il giorno che si accende il cambio password.
// Senza questa pulizia l'utente resta autenticato per il server e senza
// chiave per l'interfaccia: fermo sulla schermata di accesso, con il token
// gia' in localStorage e nessun messaggio (osservato dal vivo in tappa 2).
function pulisciDopoBustaNonApribile() {
  safeRemoveItem('zv_token');
  safeRemoveItem('zv_user');
  safeRemoveItem(SESSION_PWD_KEY, 'session');
}

// Accounts that predate the language field (or raced past register-time
// capture) come back from the server with no `language` at all — see
// safeUser() in routes/auth.js, which passes it through unmodified rather
// than defaulting it, specifically so this is detectable here. The first
// time such an account is seen (login, session restore, 2FA, unlock), adopt
// whatever's already active in the UI right now (an explicit localStorage
// choice if the visitor ever used the switcher, otherwise the browser's own
// language — see i18n/index.js's detectInitialLanguage) and persist it
// server-side once, silently. This never overrides a real choice: by the
// time `user.language` is set at all, either register-time capture or an
// actual switcher click already happened.
function withSyncedLanguage(user) {
  if (!user || user.language) return user;
  const lang = i18n.language;
  apiSetLanguage(lang).catch(() => {});
  return { ...user, language: lang };
}

// Session-restore retry policy (2026-08-30 — see restoreSession below for
// the incident this fixes). Three attempts, growing backoff between them;
// each individual attempt is capped so a hung connection can't block the
// retry loop forever. Kept as named constants, not inlined, so the numbers
// are easy to find and re-tune without hunting through the retry loop.
const RESTORE_RETRY_DELAYS_MS = [1000, 2000, 4000];
const RESTORE_ATTEMPT_TIMEOUT_MS = 8000;

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(() => { try { return JSON.parse(safeGetItem('zv_user')); } catch { return null; } });
  const [encKey, setEncKey]       = useState(null);
  const [restoring, setRestoring] = useState(false);
  // Set only once every retry in restoreSession has been exhausted by a
  // non-auth failure (network/timeout/5xx) — the session itself is left
  // completely untouched when this happens, see restoreSession's comment.
  const [restoreError, setRestoreError] = useState(false);
  const [needsUnlock, setNeedsUnlock] = useState(false);
  // Vero solo per la sessione appena aperta senza password, grazie alla
  // chiave del dispositivo: serve alla riga che lo dice all'utente.
  const [sbloccatoDalDispositivo, setSbloccatoDalDispositivo] = useState(false);
  // 2FA pending state
  const [pending2fa, setPending2fa]           = useState(null); // { pendingToken, salt, password }
  const [pending2faError, setPending2faError] = useState('');

  // Guards against a stale in-flight attempt sequence (e.g. the mount
  // effect's run is still retrying when the user taps "riprova") clobbering
  // state set by a newer one — each call to restoreSession claims a fresh id.
  const restoreRunId = useRef(0);

  // Self-contained on purpose (2026-08-30 — this whole block, its state, and
  // its constants above are grouped together and touch nothing else in this
  // file): restores a session silently on mount, called again by
  // retryRestore() below when the user taps the connection-problem screen's
  // retry button.
  //
  // Real bug this fixes: this used to treat "the token is invalid" and "the
  // network hiccuped" as the same thing — one shared catch cleared the
  // token, user and session password, and sent the visitor to the login/
  // register screen no matter which of the two actually happened. A
  // customer on a mobile connection who registered, landed in the vault,
  // then had ONE apiMe() call fail because their carrier briefly reassigned
  // their IP mid-request got treated exactly like an expired token — full
  // wipe, back to a blank signup form 51 seconds after finishing it. Traced
  // live via login_log + the Live tab's `shown` field, 2026-08-30.
  //
  // Fixed rule: only a real 401/403 response — the server explicitly saying
  // the token is invalid — clears anything. Every other failure (fetch
  // rejecting outright, a timeout, a 5xx, the backend being mid-restart) is
  // retried with growing backoff, token/user/password left completely
  // alone, and `restoring` stays true the whole time so App.js keeps showing
  // the "Ripristino sessione…" screen — never the login form, not even for
  // one render. Only once every retry has failed this way does it stop and
  // ask the user to retry by hand (restoreError) — still without touching
  // the stored session.
  // ── SBLOCCO DALLA CHIAVE DEL DISPOSITIVO (2026-09-12) ──────────────────
  //
  // QUATTRO CONDIZIONI, tutte necessarie. Nessuna guarda la larghezza dello
  // schermo o lo user-agent, che si falsificano tutti e due.
  //
  //   1. Il plugin esiste. Non e' un indizio, e' una prova: quell'oggetto lo
  //      inietta il codice nativo della nostra app. Una pagina in Chrome non
  //      ce l'ha.
  //   2. Il backup e' attivo, cioe' il Keystore ha davvero una chiave. La
  //      risposta la da' il nativo leggendo EncryptedSharedPreferences.
  //   3. La chiave e' dell'utente della sessione conservata. Un telefono su
  //      cui qualcuno ha acceso il backup e poi ha fatto accesso con un
  //      altro account non sblocca niente.
  //   4. LA CHIAVE APRE DAVVERO I FILE DI QUESTO UTENTE. Le prime tre dicono
  //      "la chiave c'e'", non "e' quella giusta": si prova su due file veri
  //      con la stessa chiamata che usa la migrazione. Il 2026-09-10 una
  //      busta perfetta conteneva una chiave che non apriva un solo file, e
  //      nessun controllo se ne era accorto.
  //
  // Se una sola condizione non vale, si cade nella schermata di sempre: non
  // esiste un caso in cui l'utente resti bloccato, il ripiego e' la password.
  const sbloccaDalDispositivo = useCallback((token, storedUser) => {
    const parsed = (() => { try { return JSON.parse(storedUser); } catch { return null; } })();
    (async () => {
      try {
        const { chiaveDalDispositivo } = await import('../utils/nativeBackup');
        const dal = await chiaveDalDispositivo();                       // condizioni 1 e 2
        if (!dal) { setNeedsUnlock(true); return; }
        if (!parsed?.username || dal.username !== parsed.username) {    // condizione 3
          setNeedsUnlock(true);
          return;
        }

        const grezza = Uint8Array.from(atob(dal.masterKey), (c) => c.charCodeAt(0));
        if (grezza.length !== 32) { setNeedsUnlock(true); return; }
        const chiave = await crypto.subtle.importKey('raw', grezza, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);

        // Condizione 4: la prova sui file veri.
        const { apiCampioni, apiMe } = await import('../utils/api');
        const risposta = await apiCampioni();
        const prova = await provaChiaveSuCampioni(chiave, risposta.campioni);
        if (prova.esito === 'non-apre' || prova.esito === 'discorde') {
          console.error('sblocco dal dispositivo: la chiave non apre i file', prova.esito);
          setNeedsUnlock(true);
          return;
        }
        // 'apre' oppure 'nessuno' (account senza file): si entra.

        const data = await apiMe();
        const syncedUser = withSyncedLanguage(data.user);
        setUser(syncedUser);
        setEncKey(chiave);
        safeSetItem('zv_user', JSON.stringify(syncedUser));
        setNeedsUnlock(false);
        // Lo dice, la prima volta: un telefono che smette di chiedere la
        // password senza spiegare perche' sembra un difetto, non una comodita'.
        setSbloccatoDalDispositivo(true);
      } catch (err) {
        console.error('sblocco dal dispositivo non riuscito:', err);
        setNeedsUnlock(true);
      }
    })();
  }, []);

  const restoreSession = useCallback(() => {
    // Percorso di montaggio: se lo storage lancia, qui cadeva tutta
    // l'applicazione. Con safeGetItem si legge null e si esce come per una
    // sessione assente — cioe' "visitatore nuovo", che porta al modulo.
    const token      = safeGetItem('zv_token');
    const storedUser = safeGetItem('zv_user');
    if (!token || !storedUser) return;

    const sessionPwd = safeGetItem(SESSION_PWD_KEY, 'session');
    if (!sessionPwd) {
      // Niente password in sessione. Due strade, in quest'ordine.
      //
      // 1) DENTRO LA NOSTRA APP, CON IL BACKUP ATTIVO: la chiave e' gia' nel
      //    Keystore per il servizio che carica le foto a telefono chiuso.
      //    Riprenderla qui non aggiunge nessuna esposizione — c'e' gia' — e
      //    toglie la password da ridigitare a ogni riapertura.
      // 2) In tutti gli altri casi: si chiede la password, come sempre.
      //    Il token resta intatto in entrambi i casi.
      sbloccaDalDispositivo(token, storedUser);
      return;
    }

    const parsedUser = (() => { try { return JSON.parse(storedUser); } catch { return null; } })();
    const runId = ++restoreRunId.current;

    setRestoring(true);
    setRestoreError(false);

    (async () => {
      for (let attempt = 0; attempt < RESTORE_RETRY_DELAYS_MS.length; attempt++) {
        if (restoreRunId.current !== runId) return; // superseded by a newer run

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), RESTORE_ATTEMPT_TIMEOUT_MS);
        try {
          const data = await apiMe(controller.signal);
          clearTimeout(timeoutId);
          const salt = data.salt || data.user?.salt || parsedUser?.salt;
          if (!salt) {
            // A successful response missing a field it should always carry
            // — not a network problem, and not a 401/403 either. Never once
            // observed in a week of real traffic when this was investigated
            // (2026-08-30); retrying can't fix a malformed server response,
            // so this one deliberate exception still falls back to "log in
            // again" rather than spinning forever.
            throw Object.assign(new Error('no salt'), { status: 401 });
          }
          // PUNTO 4 di 8 (3-ter). Attenzione 3-bis: quando il cambio
          // password si riaccendera', questo punto va tolto dalla migrazione
          // o legato a password_version — qui la password arriva da
          // sessionStorage e puo' essere vecchia.
          let key;
          try {
            key = await apriChiaveUtente(sessionPwd, salt, data.user);
          } catch (err) {
            // status 401 di proposito: e' l'unico ramo del catch qui sotto
            // che cancella la sessione invece di riprovare. Riprovare non
            // servirebbe — la busta non si apre adesso e non si aprira' al
            // terzo tentativo.
            if (err instanceof BustaNonApribile) throw Object.assign(err, { status: 401 });
            throw err;
          }
          if (restoreRunId.current !== runId) return;
          // TAPPA 3: restoreSession NON migra piu'. Vedi 3-bis del piano.
          //
          // Qui la password arriva da sessionStorage, e da quando il cambio
          // password e' possibile puo' essere VECCHIA. Migrare con una
          // password vecchia significherebbe incartare una chiave sbagliata
          // e marcarla `verificata`: la verifica di 7-bis controlla che la
          // busta si RIAPRA, non che dentro ci sia la chiave giusta. Ne
          // uscirebbe una busta perfetta piena di spazzatura.
          //
          // Aprire con la busta invece resta sicuro, ed e' quello che fa
          // apriChiaveUtente qui sopra: con la password sbagliata non si apre
          // e basta, e l'utente rifa' l'accesso.
          const syncedUser = withSyncedLanguage(data.user);
          setUser(syncedUser);
          setEncKey(key);
          safeSetItem('zv_user', JSON.stringify(syncedUser));
          setRestoring(false);
          return;
        } catch (err) {
          clearTimeout(timeoutId);
          if (restoreRunId.current !== runId) return;

          if (err.status === 401 || err.status === 403) {
            // The server itself rejected the token — the only case that
            // clears anything.
            safeRemoveItem('zv_token');
            safeRemoveItem('zv_user');
            safeRemoveItem(SESSION_PWD_KEY, 'session');
            setUser(null);
            setNeedsUnlock(false); // go to login page, not unlock modal
            setRestoring(false);
            return;
          }

          // Network error, timeout (AbortError from the controller above),
          // or a non-2xx that isn't 401/403 (502/503 mid-restart, etc.) —
          // never a reason to destroy a perfectly good token. Retry.
          if (attempt < RESTORE_RETRY_DELAYS_MS.length - 1) {
            await new Promise((r) => setTimeout(r, RESTORE_RETRY_DELAYS_MS[attempt]));
            continue;
          }

          // Retries exhausted and still no real response. Session left
          // completely untouched — just tell the UI to offer a manual retry.
          setRestoring(false);
          setRestoreError(true);
        }
      }
    })();
  }, []);

  useEffect(() => { restoreSession(); }, [restoreSession]);

  // Exposed for the connection-problem screen's retry button (App.js).
  const retryRestore = useCallback(() => { restoreSession(); }, [restoreSession]);

  const login = useCallback(async (username, password) => {
    const data = await apiLogin(username, password);

    // Server requires 2FA
    if (data.requires2fa) {
      setPending2fa({ pendingToken: data.pendingToken, salt: data.salt, password });
      return { requires2fa: true };
    }

    const syncedUser = withSyncedLanguage(data.user);
    safeSetItem('zv_token', data.token);
    safeSetItem('zv_user', JSON.stringify(syncedUser));
    safeSetItem(SESSION_PWD_KEY, password, 'session');
    // PUNTO 1 di 8 (3-ter).
    let key;
    try {
      key = await apriChiaveUtente(password, data.salt, data.user);
    } catch (err) {
      if (err instanceof BustaNonApribile) {
        pulisciDopoBustaNonApribile();
        throw new Error(i18n.t('auth:err_busta_non_apribile'));
      }
      throw err;
    }
    setUser(syncedUser); setEncKey(key); setNeedsUnlock(false);
    migraSeServe({ password, chiave: key, utente: data.user, via: 'login' });
    return { ...data, user: syncedUser };
  }, []);

  const submit2fa = useCallback(async (code) => {
    if (!pending2fa) throw new Error('No pending 2FA');
    setPending2faError('');
    try {
      const data = await api2faVerify(pending2fa.pendingToken, code);
      const syncedUser = withSyncedLanguage(data.user);
      safeSetItem('zv_token', data.token);
      safeSetItem('zv_user', JSON.stringify(syncedUser));
      safeSetItem(SESSION_PWD_KEY, pending2fa.password, 'session');
      // PUNTO 2 di 8 (3-ter).
      let key;
      try {
        key = await apriChiaveUtente(pending2fa.password, data.salt, data.user);
      } catch (err) {
        if (err instanceof BustaNonApribile) {
          pulisciDopoBustaNonApribile();
          setPending2fa(null);
          throw new Error(i18n.t('auth:err_busta_non_apribile'));
        }
        throw err;
      }
      setUser(syncedUser); setEncKey(key);
      migraSeServe({ password: pending2fa.password, chiave: key, utente: data.user, via: 'login_2fa' });
      setNeedsUnlock(false); setPending2fa(null);
      return { ...data, user: syncedUser };
    } catch (err) {
      setPending2faError(err.message);
      throw err;
    }
  }, [pending2fa]);

  const cancel2fa = useCallback(() => { setPending2fa(null); setPending2faError(''); }, []);

  // ── Cerimonia della chiave di recupero ────────────────────
  //
  // Genera la chiave e la restituisce al chiamante SENZA registrarla ancora:
  // registrarla qui significherebbe dire al server "fatto" prima che l'utente
  // l'abbia vista. Il completamento passa da confermaChiaveDiRecupero(), che
  // il componente chiama solo dopo che l'utente ha riscritto gli ultimi sei
  // caratteri.
  //
  // Il token vive nello stato del componente e in sessionStorage finche' la
  // cerimonia non e' chiusa: se l'utente ricarica la pagina lo ritrova invece
  // di perderlo — che e' esattamente il difetto del vecchio schermo.
  const CHIAVE_IN_CORSO = 'zv_chiave_in_corso';

  const preparaChiaveDiRecupero = useCallback(async () => {
    const esistente = safeGetItem(CHIAVE_IN_CORSO, 'session');
    if (esistente) return esistente;
    const token = generateRecoveryToken();
    safeSetItem(CHIAVE_IN_CORSO, token, 'session');
    return token;
  }, []);

  const confermaChiaveDiRecupero = useCallback(async (token) => {
    if (!token) throw new Error('nessuna chiave da confermare');
    // Serve il salt dell'utente per derivare la chiave di recupero, e la
    // chiave AES gia' in memoria per cifrarne il backup. Entrambi ci sono:
    // siamo dentro una sessione aperta, non alla registrazione.
    const { apiMe, apiRecoverySetup } = await import('../utils/api');
    const me = await apiMe();
    const salt = me.salt || me.user?.salt;
    if (!salt) throw new Error('salt non disponibile');
    if (!encKey) throw new Error('chiave di sessione non disponibile');

    const recoveryKey = await deriveRecoveryKey(token, salt);
    const keyBackup = await encryptKeyWithRecovery(encKey, recoveryKey);
    await apiRecoverySetup(token, keyBackup);

    safeRemoveItem(CHIAVE_IN_CORSO, 'session');
    setUser((u) => {
      const agg = { ...(u || {}), recoveryKeyConfirmed: true, hasRecoveryToken: true };
      safeSetItem('zv_user', JSON.stringify(agg));
      return agg;
    });
    return true;
  }, [encKey]);

  // No username param — the register form only collects email + password;
  // the server derives a unique username from the email (routes/auth.js)
  // and returns it on `data.user.username`.
  // LA CHIAVE DI RECUPERO NON SI GENERA PIU' QUI (2026-09-04).
  //
  // Fino a oggi veniva creata alla registrazione e mostrata subito, in uno
  // schermo che compariva una volta sola: chi ricaricava la pagina la perdeva
  // per sempre, e chi arrivava dal browser interno di Facebook spesso la
  // chiudeva senza capire cosa fosse. Misurato: dei 162 utenti in browser
  // in-app, il 49% abbandonava entro 77 secondi dalla registrazione, e il
  // tasso di pagamento era 9% contro il 23% di chi usa un browser normale.
  //
  // Ora la registrazione non ha piu' ostacoli, e la cerimonia avviene al
  // PRIMO CARICAMENTO — dove l'utente sta gia' usando il prodotto e ha un
  // motivo per capire perche' gli serve una chiave. Vedi
  // components/CerimoniaChiave.jsx e /auth/recovery/setup.
  const register = useCallback(async (email, password, emailCheckMeta = {}) => {
    const salt = generateSalt();
    const key = await deriveKey(password, salt);

    const { source, campaign, medium, utmSource, fbclid, rdtCid, gclid, ttclid, referrer, landingPage, attributionCapturedAt } = getAttribution();
    const referralCode = getReferralCode();
    // keyBackup e recoveryToken partono NULL: la chiave verra' generata e
    // registrata al primo caricamento, via /auth/recovery/setup. Il server
    // accetta entrambi assenti (routes/auth.js: `recoveryTokenRaw ? ... : null`).
    const data = await apiRegister(password, salt, email, null, null, source, campaign, referralCode, { medium, utmSource, fbclid, rdtCid, gclid, ttclid, referrer, landingPage, attributionCapturedAt }, i18n.language, emailCheckMeta);
    safeSetItem('zv_token', data.token);
    safeSetItem('zv_user', JSON.stringify(data.user));
    safeSetItem(SESSION_PWD_KEY, password, 'session');
    setUser(data.user); setEncKey(key); setNeedsUnlock(false);
    // PUNTO 5 di 8 (3-ter). Un account nuovo NON nasce con una master key
    // casuale in questa tappa, e il motivo sta in fondo a
    // utils/chiavePrincipale.js: register e /migrate-master-key sono due
    // scritture separate, e con una chiave casuale il fallimento della
    // seconda renderebbe illeggibili i file appena caricati. Con la chiave
    // derivata, lo stesso fallimento non fa alcun danno.
    migraSeServe({ password, chiave: key, utente: data.user, via: 'register' });

    // Registration confirmed by the server (not just a button click) — fire once,
    // using the SAME event_id string the server already used for its own CAPI
    // call (data.metaEventId, computed once by metaConversions.registrationEventId
    // — see its comment, 2026-08-30) instead of recomputing the formula here.
    if (typeof window.fbq === 'function') {
      // `undefined`, NOT `{}`, for the custom-data slot (2026-08-30). Meta's
      // Events Manager reported it was rejecting 100% of our
      // CompleteRegistration events for `currency` arriving as an empty
      // string — and nothing in this codebase ever sets a currency on this
      // event. It comes from fbevents.js serialising an empty object
      // against the standard event's own parameter schema and emitting the
      // declared-but-unset keys as blanks. CompleteRegistration needs no
      // currency at all, and an omitted field is not the same as a blank
      // one: passing undefined means no custom data is serialised.
      window.fbq('track', 'CompleteRegistration', undefined, { eventID: data.metaEventId });
    }
    if (typeof window.rdt === 'function') {
      window.rdt('track', 'SignUp');
    }
    // Google Ads registration conversion (2026-08-31). Fired from here, not
    // from a page-load tag: this is a React SPA, the URL changes without a
    // reload, so a conversion bound to "someone loaded /thank-you" would
    // never fire at all. This line runs at the one moment the server has
    // actually confirmed the account exists — the same moment the Meta and
    // Reddit events above go out. One place, three destinations.
    //
    // The `typeof window.gtag === 'function'` guard IS the hostname check,
    // and it is the same mechanism fbq and rdt rely on two lines up: gtag()
    // is declared *inside* the hostname gate in public/index.html, so off
    // lifetimecloud.me / www (the Android WebView's https://localhost, a dev
    // server, staging) it is never assigned and this block is skipped.
    // Verified by executing the built, minified page against each hostname.
    // Deliberately not re-listing the hostnames here: a second copy of that
    // list is exactly how two sources drift apart.
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-18420798140/j5efCPWUvescELyl3M9E',
        value: 1.0,
        currency: 'EUR',
      });
    }

    // REGRESSIONE del 2026-09-04 sera (commit 348b676), trovata dal vivo:
    // spostando la cerimonia della chiave era stata tolta la const
    // recoveryToken ma NON questo return, che la referenziava ancora —
    // ReferenceError a funzione quasi finita, DOPO setUser: l'utente
    // risultava registrato e autenticato, ma il chiamante (AuthPage)
    // finiva nel catch e onRegistered non partiva mai. Risultato: chi
    // cliccava "Acquista" su un piano atterrava nel vault senza mai
    // vedere il checkout. Il return restituisce solo `data`: la chiave
    // di recupero non nasce piu' qui.
    return data;
  }, []);

  // StackSocial redemption — isolated duplicate of register()'s crypto
  // orchestration on purpose (see the project plan): same generateSalt/
  // deriveKey/recovery-token sequence, zero shortcuts on the zero-knowledge
  // vault, just posted to a different endpoint that also consumes a code.
  // No Stripe/attribution wiring here — this account's plan comes from the
  // redemption transaction itself, not a payment webhook.
  const redeemStackSocialCode = useCallback(async (email, password, code, emailCheckMeta = {}) => {
    const salt = generateSalt();
    const recoveryToken = generateRecoveryToken();
    const key = await deriveKey(password, salt);
    const recoveryKey = await deriveRecoveryKey(recoveryToken, salt);
    const keyBackup = await encryptKeyWithRecovery(key, recoveryKey);

    const data = await apiRedeemStackSocialCode(email, password, salt, code, keyBackup, recoveryToken, i18n.language, emailCheckMeta);
    safeSetItem('zv_token', data.token);
    safeSetItem('zv_user', JSON.stringify(data.user));
    safeSetItem(SESSION_PWD_KEY, password, 'session');
    safeSetItem('zv_pending_recovery', recoveryToken, 'session');
    safeSetItem('zv_pending_recovery_user', data.user.username, 'session');
    setUser(data.user); setEncKey(key); setNeedsUnlock(false);
    // PUNTO 6 di 8 (3-ter). Duplicato deliberato di register (vedi sopra):
    // ogni modifica al punto 5 va replicata QUI, nella stessa sessione.
    // E' il punto che si dimentica.
    migraSeServe({ password, chiave: key, utente: data.user, via: 'stacksocial' });

    return { ...data, recoveryToken };
  }, []);

  // Authenticated variant of redeemStackSocialCode above (2026-08-30) —
  // applies a code to the account already signed in here, no new
  // password/encryption-key generation involved (routes/stacksocial.js's
  // /redeem-self only ever touches plan/activePlans, never salt/
  // passwordHash/encryptedKeyBackup). Updates local user state via the same
  // refreshUser() every other plan-changing flow uses, so the vault picks
  // up the new plan immediately without a full reload.
  const redeemStackSocialCodeSelf = useCallback(async (code) => {
    const data = await apiRedeemStackSocialCodeSelf(code);
    refreshUser(data.user);
    return data;
  }, []);

  const unlockWithPassword = useCallback(async (password) => {
    const storedUser = JSON.parse(safeGetItem('zv_user'));
    if (!storedUser?.username) throw new Error('Session expired — please log in again');

    // Verify password by doing a real login — this also gets fresh salt
    const loginData = await apiLogin(storedUser.username, password);

    // If 2FA required, handle it
    if (loginData.requires2fa) {
      setPending2fa({ pendingToken: loginData.pendingToken, salt: loginData.salt, password });
      setNeedsUnlock(false);
      return;
    }

    // Password correct — update token and derive key
    const syncedUser = withSyncedLanguage(loginData.user);
    safeSetItem('zv_token', loginData.token);
    safeSetItem('zv_user', JSON.stringify(syncedUser));
    safeSetItem(SESSION_PWD_KEY, password, 'session');
    // PUNTO 3 di 8 (3-ter).
    let key;
    try {
      key = await apriChiaveUtente(password, loginData.salt, loginData.user);
    } catch (err) {
      if (err instanceof BustaNonApribile) {
        pulisciDopoBustaNonApribile();
        setUser(null); setEncKey(null); setNeedsUnlock(false);
        throw new Error(i18n.t('auth:err_busta_non_apribile'));
      }
      throw err;
    }
    setUser(syncedUser); setEncKey(key); setNeedsUnlock(false);
    migraSeServe({ password, chiave: key, utente: loginData.user, via: 'unlock' });
  }, []);

  // setEncKey(null) is the actual "zeroing" of the master key: encKey is a
  // non-extractable CryptoKey (see deriveKey in utils/crypto.js), so its raw
  // bytes were never readable/copyable from JS in the first place — dropping
  // the last reference is the only, and sufficient, way to make it collectible.

  // ── PUNTO 7 di 8 (tappa 3) — CAMBIO PASSWORD ─────────────────────────────
  //
  // Prima questa strada esisteva ma era spenta, e non per prudenza: cosi'
  // com'era distruggeva i dati. Derivava una chiave nuova dalla password
  // nuova e la lasciava li', mentre i file restavano cifrati con quella
  // vecchia. Chi la percorreva rientrava nel proprio account e non apriva
  // piu' un file.
  //
  // Adesso la chiave dei file NON CAMBIA. Cambia solo l'involucro che la
  // protegge. Si vede da una riga che non c'e': dopo questa funzione encKey
  // resta esattamente l'oggetto di prima, e nessun file viene riletto,
  // ricifrato o riscritto.
  const cambiaPassword = useCallback(async (passwordAttuale, nuovaPassword, altriCampi = {}) => {
    if (!encKey) throw new Error(i18n.t('auth:err_cambio_password_senza_chiave'));

    // La busta nuova si prepara e si VERIFICA prima di parlare col server.
    // Se non si riapre, reincartaConNuovaPassword lancia e qui non si manda
    // niente: meglio un cambio password che non avviene di uno che avviene
    // a meta'.
    const { wrapSalt, wrappedMasterKey } = await reincartaConNuovaPassword(encKey, nuovaPassword);
    // Niente nuovo salt (2026-09-16): `salt` e' il sale della copia di recupero,
    // ruotarlo la rendeva inapribile. Vedi routes/auth.js, update-profile.

    const data = await apiUpdateProfile({
      ...altriCampi,
      currentPassword: passwordAttuale,
      newPassword: nuovaPassword,
      newWrapSalt: wrapSalt,
      newWrappedMasterKey: wrappedMasterKey,
    });

    // Solo DOPO che il server ha confermato. Scriverla prima significherebbe,
    // se la chiamata fallisse, lasciare in sessione una password che non e'
    // quella dell'account: il prossimo restoreSession non aprirebbe la busta
    // e l'utente si troverebbe buttato fuori senza aver cambiato niente.
    safeSetItem(SESSION_PWD_KEY, nuovaPassword, 'session');
    if (data.user) {
      const syncedUser = withSyncedLanguage(data.user);
      setUser(syncedUser);
      safeSetItem('zv_user', JSON.stringify(syncedUser));
    }
    // encKey NON si tocca: e' la stessa chiave di prima, ed e' il punto.
    return data;
  }, [encKey]);

  // ── PUNTO 8 di 8 (tappa 3) — RIPRISTINO DA CHIAVE DI RECUPERO ────────────
  //
  // E' la strada che riporta dentro chi ha perso la password, ed e' anche
  // l'unica irreversibile: quando finisce, la vecchia password non esiste
  // piu'. Per questo l'ordine dei passi non e' negoziabile — si verifica
  // TUTTO prima di scrivere QUALSIASI cosa:
  //
  //   1. il token di recupero apre la copia della chiave?      se no, stop
  //   2. quella chiave apre un file VERO dell'account?         se no, stop
  //   3. la busta nuova si riapre?                             se no, stop
  //   4. solo adesso si sovrascrive la password
  //
  // Il passo 2 e' quello che nessuno farebbe spontaneamente, ed e' quello che
  // serve: il passo 1 dimostra che la copia e' integra, non che la chiave
  // dentro sia giusta (i 43 account con la copia corrotta di 5-bis).
  const ripristinaDaChiaveDiRecupero = useCallback(async (username, tokenDiRecupero, nuovaPassword) => {
    // 1) la copia si apre?
    const dati = await apiRecoveryVerify(username, tokenDiRecupero);
    if (!dati.encryptedKeyBackup) throw new Error(i18n.t('auth:err_recupero_senza_copia'));
    const recoveryKey = await deriveRecoveryKey(tokenDiRecupero, dati.salt);
    const masterKey = await recuperaChiaveDaBackup(dati.encryptedKeyBackup, recoveryKey);

    // 2) la chiave apre file VERI? Due campioni, il piu' vecchio e il piu'
    //    nuovo: se questo account ha i file spezzati fra due chiavi, la
    //    frattura cade fra i due e si vede.
    let prova;
    try {
      const risposta = await apiCampioneRecupero(username, tokenDiRecupero);
      prova = await provaChiaveSuCampioni(masterKey, risposta.campioni);
    } catch (err) {
      // Non riuscire a CHIEDERE i campioni non e' come scoprire che la chiave
      // e' sbagliata: la prima e' rete, la seconda e' un dato rotto. Non si
      // mescolano, e nel dubbio non si scrive.
      console.error('ripristino: campioni non ottenuti', err);
      throw new Error(i18n.t('auth:err_recupero_prova_impossibile'));
    }
    if (prova.esito === 'non-apre')
      throw new Error(i18n.t('auth:err_recupero_chiave_sbagliata'));
    if (prova.esito === 'discorde') {
      // Qui NON si sovrascrive niente. Con la chiave sbagliata non si
      // aprirebbe nessun campione: se uno si apre e uno no, o un file e'
      // corrotto o questo account ha due chiavi. In entrambi i casi la
      // password vecchia e' l'unica cosa che permette ancora di riprovare, e
      // non la si butta via per andare avanti alla cieca.
      console.error('ripristino: campioni discordi', prova.dettaglio);
      throw new Error(i18n.t('auth:err_recupero_campioni_discordi'));
    }
    const esitoProva = prova.esito === 'nessuno' ? 'nessun-file' : 'apre';

    // 3) la busta nuova, verificata prima di partire
    const { wrapSalt, wrappedMasterKey } = await reincartaConNuovaPassword(masterKey, nuovaPassword);
    const nuovoSalt = generateSalt();

    // La chiave di recupero si consuma: quella vecchia e' appena passata per
    // una rete che potrebbe averla vista, e comunque l'utente l'ha usata.
    // Se ne genera una nuova e si protegge con lei LA STESSA master key.
    const nuovoToken = generateRecoveryToken();
    const nuovaRecoveryKey = await deriveRecoveryKey(nuovoToken, nuovoSalt);
    const nuovaCopia = await encryptKeyWithRecovery(masterKey, nuovaRecoveryKey);

    // 4) adesso, e non prima, si scrive
    const data = await apiRecoveryReset({
      username,
      recoveryToken: tokenDiRecupero,
      newPassword: nuovaPassword,
      newSalt: nuovoSalt,
      newWrapSalt: wrapSalt,
      newWrappedMasterKey: wrappedMasterKey,
      newEncryptedKeyBackup: nuovaCopia,
      newRecoveryTokenHash: nuovoToken,
    });

    if (data.token) {
      const syncedUser = withSyncedLanguage(data.user);
      safeSetItem('zv_token', data.token);
      safeSetItem('zv_user', JSON.stringify(syncedUser));
      safeSetItem(SESSION_PWD_KEY, nuovaPassword, 'session');
      setUser(syncedUser); setEncKey(masterKey); setNeedsUnlock(false);
    }
    // `esitoProva` torna a chi chiama: se vale 'nessun-file' la prova del
    // passo 2 non e' stata fatta perche' non c'era niente da provare, e
    // all'utente va detto — non si spaccia un controllo saltato per un
    // controllo superato.
    return { ...data, nuovoToken, provaSuFileVero: esitoProva };
  }, []);

  const clearSession = useCallback(() => {
    safeRemoveItem('zv_token');
    safeRemoveItem('zv_user');
    safeRemoveItem(SESSION_PWD_KEY, 'session');
    setUser(null); setEncKey(null);
    setNeedsUnlock(false); setPending2fa(null);
  }, []);

  const refreshUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    safeSetItem('zv_user', JSON.stringify(updatedUser));
  }, []);

  // Passa da safeGetItem (utils/storage.js) e non piu' da localStorage nudo.
  // Questa riga viene eseguita durante il render di AuthProvider, che avvolge
  // tutta l'applicazione: nel browser interno di Facebook, dove localStorage
  // puo' LANCIARE invece di rispondere, faceva cadere il render e il
  // visitatore vedeva una pagina bianca. Difetto presente dal primo commit,
  // riprodotto e verificato il 2026-09-02.
  //
  // Se lo storage non risponde il risultato e' false, cioe' "non risulta
  // nessuna sessione": e' il valore che porta al modulo di registrazione, mai
  // al login. Vedi la regola scritta in utils/storage.js.
  const hasStoredSession = !!(safeGetItem('zv_token') && safeGetItem('zv_user'));

  return (
    <AuthContext.Provider value={{
      user, encKey, restoring, restoreError, needsUnlock, hasStoredSession, sbloccatoDalDispositivo,
      pending2fa, pending2faError,
      login, register, redeemStackSocialCode, redeemStackSocialCodeSelf, clearSession, refreshUser,
      cambiaPassword, ripristinaDaChiaveDiRecupero,
      unlockWithPassword, submit2fa, cancel2fa, retryRestore,
      preparaChiaveDiRecupero, confermaChiaveDiRecupero,
      isAuthenticated: !!user && !!encKey,
      isAdmin: user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
